import type { Specimen } from "@src/packages/care/cryovault/useSpecimens";
import { CONSUMING_SPECIMEN_USAGES } from "@src/packages/care/cryovault/frozenSpecimenUsages";

export type IuiSpecimenLinkageStatus =
  "linked" | "available" | "awaiting_sample" | "unavailable";

export type IuiSpecimenLinkage = {
  status: IuiSpecimenLinkageStatus;
  linkedSpecimen: Specimen | null;
  availableSpecimens: Specimen[];
};

const BLOCKED_SPECIMEN_STATUSES = new Set(["used", "discarded", "donated"]);

function isAvailableSpecimen(specimen: Specimen): boolean {
  return !BLOCKED_SPECIMEN_STATUSES.has(specimen.status);
}

/**
 * Resolve the patient-visible sample state without guessing a sample from its
 * order in the response. A sample is linked only when the phase persisted its
 * exact specimen ID; cycle reservations remain useful context but do not
 * silently become a procedure link.
 */
export function resolveIuiSpecimenLinkage({
  specimens,
  cycleId,
  linkedSpecimenId,
}: {
  specimens: readonly Specimen[] | undefined;
  cycleId: string | null | undefined;
  linkedSpecimenId: string | null | undefined;
}): IuiSpecimenLinkage {
  const availableSpecimens = (specimens ?? []).filter(isAvailableSpecimen);
  const linkedSpecimen = linkedSpecimenId
    ? ((specimens ?? []).find((specimen) => specimen.id === linkedSpecimenId) ??
      null)
    : null;

  if (linkedSpecimenId && !linkedSpecimen) {
    return {
      status: "unavailable",
      linkedSpecimen: null,
      availableSpecimens,
    };
  }

  if (linkedSpecimen) {
    return {
      status: isAvailableSpecimen(linkedSpecimen) ? "linked" : "unavailable",
      linkedSpecimen: isAvailableSpecimen(linkedSpecimen)
        ? linkedSpecimen
        : null,
      availableSpecimens,
    };
  }

  const hasCycleReservation = availableSpecimens.some((specimen) =>
    (specimen.specimenUsages ?? []).some(
      (usage) =>
        usage.cycleId === cycleId && CONSUMING_SPECIMEN_USAGES.has(usage.usage),
    ),
  );

  return {
    status:
      availableSpecimens.length > 0 || hasCycleReservation
        ? "available"
        : "awaiting_sample",
    linkedSpecimen: null,
    availableSpecimens,
  };
}
